/**
 * audit_events — tamper-evident audit trail (master spec §32).
 *
 * Phase 2 declares the table SHAPE; Phase 5 begins INSERTing rows. The
 * table is RLS-enforced and append-only IN THE DATABASE since migration 0165
 * (DEC-338): app_user has no UPDATE/DELETE (UPDATE on `payload` only, for the
 * DEC-301 hourly counter), and the `audit_append_only_guard` trigger refuses
 * UPDATE/DELETE/TRUNCATE by any other role except FK SET NULL / CASCADE actions.
 *
 * Why declare in Phase 2 (RESEARCH §audit_events lines 273-321):
 *   - Master spec §32 fully specifies the shape — no Phase 5 design
 *     ambiguity to wait on.
 *   - Phase 5 will need foreign-key references (actor_id → users.id, etc.) —
 *     declaring the table now keeps Phase 5's migration narrow.
 *   - Phase 5 will INSERT into audit_events from inside the same transaction
 *     as the business-logic write — having the table already present means
 *     Phase 5 plans can be more focused.
 *   - Cost is one extra pgTable + one CREATE TABLE in 0000_init. Trivial.
 *
 * RLS posture: standard tenant-scoped predicate. T-02-04-01 family (HIGH) —
 * cross-tenant audit-event reads are blocked by RLS even with a known PK.
 *
 * previous_hash chain (T-02-04-04 mitigation, DEC-320): declared here in
 * Phase 2, populated since migration 0127 by a BEFORE INSERT trigger
 * (`audit_events_chain_link`), not by any writer. One chain per tenant:
 * `chain_seq` orders it (occurred_at is device time, so it cannot), and
 * previous_hash is the SHA256 of EVERY column of the prior row, from
 * `audit_event_chain_hash()`. The first chained row links to 64 zeros; rows
 * written before 0127 keep both NULL and sit outside the chain.
 * `audit_chain_verify(tenant)` walks it. OPERATOR_API_READ_HOURLY rows are
 * never chained — they are counters, updated after insert (DEC-301).
 *
 * FK null-outs (DEC-344, migration 0169): when a user, job or ticket is
 * hard-deleted, the FK SET NULL on a chained row is recorded by an
 * AUDIT_ROW_REDACTED event appended to the same chain (by a trigger, never by
 * application code), and audit_chain_verify rebuilds the row's original hash
 * from it. Rows chained since 0169 (`chain_hash_v` = 2) hash those three FK
 * columns as salted digests, so the rebuild is exact; older rows carry an
 * attested before/after hash.
 *
 * Allowlist storage rationale (RESEARCH Open Question #1): event_type,
 * actor_type, device_mode, identity_source are stored as `text` with CHECK
 * constraints rather than pgEnum. Master spec §32's event_type list will
 * grow (Phase 5+ adds new event types regularly); pgEnum ALTER is fragile
 * (drizzle issue #4295), text+CHECK rewrites are routine. event_type has
 * NO CHECK constraint at this stage — Phase 5 may add an ALTER CHECK once
 * the canonical event_type list stabilizes. actor_type and device_mode have
 * tightly-locked allowlists from §32 and ship the CHECK now.
 *
 * Indexes (RESEARCH #21/#22/#23):
 *   - (tenant_id, ticket_id, occurred_at DESC) — ticket audit timeline.
 *   - (tenant_id, actor_id, occurred_at DESC) — "what did actor X do" view.
 *   - (tenant_id, event_type, occurred_at DESC) — event-type filter.
 *
 * FK posture:
 *   - tenant_id → tenants.id (cascade): standard.
 *   - ticket_id → haul_captures.id (set null): preserves audit trail when
 *     a ticket is hard-deleted (in practice tickets are soft-deleted).
 *   - job_id → jobs.id (set null): same rationale.
 *   - actor_id → users.id (set null): preserves audit trail when an actor's
 *     user row is deleted (e.g. employee offboarded years later).
 *   - The three set-null columns are exactly what 0169 records (DEC-344);
 *     a new set-null FK must join its digest encoding and redaction trigger.
 *
 * Sources:
 *   - master spec §32 (AUDIT TRAIL — full event_type / actor_type /
 *     identity_source lists; payload JSONB; previous_hash SHA256 chain)
 *   - RESEARCH §audit_events (Phase-2 declaration recommendation)
 *   - RESEARCH Performance Indexes #21-23
 *   - RESEARCH Open Question #1 (text + CHECK over pgEnum for evolving lists)
 *   - RESEARCH Open Question #3 (previous_hash declared, populated Phase 5)
 *   - CONTEXT D-15 (RLS), D-16 (platform-catalog tables NOT this table —
 *     audit_events IS tenant-scoped)
 *   - CONTEXT 'Claude's Discretion' § (Phase-2 vs Phase-5 declaration —
 *     RESEARCH says yes, declare here)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - actor_type allowlist is exactly 7 values (master spec §32 + DEC-293):
 *     DISPATCHER / DRIVER / CUSTOMER / SYSTEM / TRUCK_PUSHER / BILLING,
 *     plus OPERATOR_API (Phase 18 / Task 18.2, migration 0113).
 *   - device_mode allowlist is NULL OR exactly 2 values:
 *     PERSONAL / SHARED.
 *   - audit_events is APPEND-ONLY; no code may UPDATE or DELETE it. Since
 *     0165 (DEC-338) the database enforces it — see the header.
 *   - previous_hash is varchar(64) for SHA256 hex. previous_hash and
 *     chain_seq are written ONLY by the 0127 trigger, which overwrites
 *     whatever a caller supplies (DEC-320).
 */

import {
  bigint,
  boolean,
  check,
  index,
  integer,
  jsonb,
  numeric,
  pgPolicy,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';
import { haulCaptures } from './hauls.js';

/**
 * AUDIT_ACTOR_TYPES — 7 values: master spec §32's original six, plus
 * OPERATOR_API (DEC-293).
 *
 * Stored as text + CHECK at the column level, mirrored by
 * `audit_events_actor_type_check` — 0000_init created it with six values and
 * migration 0113 widened it to these seven. The two must agree: a value here
 * that the CHECK rejects fails at INSERT, and a value the CHECK admits but this
 * const omits is a row no TypeScript reader can narrow.
 *
 * Adding an 8th value requires its own decision. OPERATOR_API got one because
 * `18-PLAN.md` assumed an 'API' value already existed (it did not — the value it
 * was remembering is 'EXTERNAL_API' on the unbuilt PlatformActivityLog, a
 * different table with a different taxonomy), so the name had to be chosen
 * rather than inherited. 'OPERATOR_API' over 'API': every mobile and web call
 * also arrives through "the API", so 'API' would read as all programmatic
 * traffic while meaning one surface.
 */
export const AUDIT_ACTOR_TYPES = [
  'DISPATCHER',
  'DRIVER',
  'CUSTOMER',
  'SYSTEM',
  'TRUCK_PUSHER',
  'BILLING',
  /**
   * DEC-293 — an authenticated Operator Public API call (Phase 18). `actor_id`
   * is NULL on these rows: the actor is a credential, not a person. The
   * credential is identified by `payload.keyId` / `payload.credentialId`, which
   * are the non-secret halves; no secret or hash is ever in an audit payload.
   */
  'OPERATOR_API',
] as const;
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number];

/**
 * AUDIT_DEVICE_MODES — exactly 2 values per master spec §32. NULL is
 * allowed (non-device events such as system-emitted billing audit lines).
 */
export const AUDIT_DEVICE_MODES = ['PERSONAL', 'SHARED'] as const;
export type AuditDeviceMode = (typeof AUDIT_DEVICE_MODES)[number];

/**
 * audit_events — tamper-evident audit trail per master spec §32.
 *
 * Append-only at the application layer (Phase 5+ enforces). RLS-enforced.
 * 3 perf indexes (RESEARCH #21/#22/#23). 2 typed CHECK constraints
 * (actor_type, device_mode). Hash-chained per tenant since 0127 (DEC-320).
 */
export const auditEvents = pgTable(
  'audit_events',
  {
    eventId: uuid('event_id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // Nullable FKs — non-ticket events (e.g. login) have no ticket; some
    // events have no associated job. onDelete: set null preserves the audit
    // trail when the referenced row is hard-deleted.
    ticketId: uuid('ticket_id').references(() => haulCaptures.id, { onDelete: 'set null' }),
    jobId: uuid('job_id').references(() => jobs.id, { onDelete: 'set null' }),
    // Master spec §32 — text + CHECK at Phase 5 once the canonical list
    // stabilizes. RESEARCH Open Question #1 — evolving list.
    eventType: text('event_type').notNull(),
    // Master spec §32 + DEC-293 — actor_type allowlist (7 values).
    // CHECK constraint defined below in the (t) => [...] callback.
    actorType: text('actor_type').notNull(),
    // Nullable — system-emitted events have no human actor.
    actorId: uuid('actor_id').references(() => users.id, { onDelete: 'set null' }),
    // Device-local time the event happened.
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    // Server-receive timestamp (sync-ordering analysis).
    syncedAt: timestamp('synced_at', { withTimezone: true }).notNull().defaultNow(),
    deviceId: uuid('device_id'),
    // CHECK constraint defined below in the (t) => [...] callback.
    deviceMode: text('device_mode'),
    gpsLat: numeric('gps_lat', { precision: 10, scale: 7 }),
    gpsLng: numeric('gps_lng', { precision: 10, scale: 7 }),
    gpsAccuracyFt: integer('gps_accuracy_ft'),
    onlineFlag: boolean('online_flag'),
    // Master spec §32 — event-specific data shape.
    payload: jsonb('payload'),
    // Master spec §32 — SHA256 chain (DEC-320). Set by the 0127 trigger from
    // the tenant's chain head; never written by application code.
    previousHash: varchar('previous_hash', { length: 64 }),
    // DEC-320 — position in the tenant's chain. NULL on pre-0127 rows and on
    // OPERATOR_API_READ_HOURLY rows. Set by the 0127 trigger only.
    chainSeq: bigint('chain_seq', { mode: 'number' }),
    // DEC-344 — the row's hash encoding. NULL = v1 (0127; every row chained before
    // 0169), 2 = the FK columns hashed as salted digests so an FK ON DELETE SET NULL
    // can be verified from its AUDIT_ROW_REDACTED record. Set by the chain trigger only.
    chainHashV: smallint('chain_hash_v'),
    // Master spec §32 — how the actor was authenticated.
    // 'COGNITO_PHONE' | 'DEVICE_PIN' | 'EMAIL_PASSWORD' | etc.
    identitySource: text('identity_source'),
  },
  (t) => [
    // RESEARCH #21 — ticket audit timeline (newest first).
    index('audit_events_tenant_ticket_occurred_idx').on(
      t.tenantId,
      t.ticketId,
      t.occurredAt.desc(),
    ),
    // RESEARCH #22 — actor activity view ("what did actor X do").
    index('audit_events_tenant_actor_occurred_idx').on(t.tenantId, t.actorId, t.occurredAt.desc()),
    // RESEARCH #23 — event-type filter (e.g. "all GEOFENCE_ENTER events").
    index('audit_events_tenant_event_type_occurred_idx').on(
      t.tenantId,
      t.eventType,
      t.occurredAt.desc(),
    ),
    // Master spec §32 + DEC-293 — actor_type allowlist (7 values). Widened from
    // six by migration 0113, which is authoritative for the live constraint.
    check(
      'audit_events_actor_type_check',
      sql`actor_type IN ('DISPATCHER', 'DRIVER', 'CUSTOMER', 'SYSTEM', 'TRUCK_PUSHER', 'BILLING', 'OPERATOR_API')`,
    ),
    // Master spec §32 — device_mode allowlist (NULL or 2 values).
    check(
      'audit_events_device_mode_check',
      sql`device_mode IS NULL OR device_mode IN ('PERSONAL', 'SHARED')`,
    ),
    // DEC-320 — fork backstop: two rows can never claim one link.
    uniqueIndex('audit_events_tenant_chain_seq_uniq')
      .on(t.tenantId, t.chainSeq)
      .where(sql`chain_seq IS NOT NULL`),
    pgPolicy('audit_events_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

/**
 * audit_chain_heads — DEC-320, migration 0127. One row per tenant: the last
 * chain_seq handed out and the hash of that row. The 0127 trigger locks this
 * row for every chained insert, which is what keeps concurrent writers from
 * forking the chain, and audit_chain_verify() checks the newest row against
 * it. app_user may only SELECT its own tenant's row; the trigger (SECURITY
 * DEFINER) is the only writer.
 */
export const auditChainHeads = pgTable(
  'audit_chain_heads',
  {
    tenantId: uuid('tenant_id')
      .primaryKey()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    lastSeq: bigint('last_seq', { mode: 'number' }).notNull(),
    lastHash: varchar('last_hash', { length: 64 }).notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  () => [
    pgPolicy('audit_chain_heads_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'select',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type AuditEvent = typeof auditEvents.$inferSelect;
export type NewAuditEvent = typeof auditEvents.$inferInsert;
export const auditEventSelectSchema = createSelectSchema(auditEvents);
export const auditEventInsertSchema = createInsertSchema(auditEvents);
